// ============================================================
// 🧬 Character 模型 — 战斗角色（单/多人通用）
// ============================================================
// 设计原则：
// - 同时支持单人和多人：单人时 S 即为 Character 实例
// - 所有战斗函数都可以接受 character 参数，默认 fallback 到 S
// - 属性向后兼容，零破坏现有代码

import { CLASSES } from './classes.js';
import { getEquipById } from './equipment.js';

export class Character {
  constructor(data = {}) {
    // 基础身份
    this.name   = data.name   || '';
    this.cls    = data.cls    || null;
    this.level  = data.level  || 1;
    this.gold   = data.gold   || 0;
    this.exp    = data.exp    || 0;
    this.expNeed = data.expNeed || 50;
    this.fatigue  = data.fatigue  || 156;
    this.maxFatigue = data.maxFatigue || 156;

    // 核心战斗属性
    this.hp     = data.hp     ?? 100;
    this.maxHp  = data.maxHp  ?? 100;
    this.mp     = data.mp     ?? 50;
    this.maxMp  = data.maxMp  ?? 50;
    this.atk    = data.atk    ?? 10;
    this.def    = data.def    ?? 5;
    this.spd    = data.spd    ?? 5;

    // 装备系统
    this.equipment  = data.equipment  || {};
    this.inventory  = data.inventory  || [];
    this.ownedEquipIds = data.ownedEquipIds || [];
    this.enhanceLevels  = data.enhanceLevels  || {};
    this.weaponPenalties = data.weaponPenalties || {};
    this.altarBuff  = data.altarBuff  || false;

    // 战斗运行时状态
    this.statusEffects  = data.statusEffects  || {};
    this.passiveProc    = data.passiveProc    || false;
    this.npcBoosts      = data.npcBoosts      || { atk:0, def:0, spd:0 };

    // 技能系统
    this._skillCooldowns = data._skillCooldowns || {};
    this._skillUpgrades  = data._skillUpgrades  || {};
    this._lastSkillId    = data._lastSkillId    || null;
    this._activeBuffs    = data._activeBuffs    || [];

    // DOT / 召唤物
    this._activeDebuffs = data._activeDebuffs || {};
    this._summonData    = data._summonData    || null;
    this._burnTurns     = data._burnTurns     || 0;
    this._burnDmg       = data._burnDmg       || 0;

    // 护盾/无敌
    this._shieldTurns = data._shieldTurns || 0;
    this._invincTurns = data._invincTurns || 0;
    this._hotTurns    = data._hotTurns    || 0;
    this._hotHeal     = data._hotHeal     || 0;
  }

  // ── 计算有效攻击力 ──
  getEffAtk() {
    const stats = this._calcEquipStats();
    let atk = this.atk + stats.bonusAtk + stats.bonusStr;
    if (this.cls === 'berserk' && this.hp < this.maxHp * 0.5) atk = Math.floor(atk * 1.4);
    if (this.statusEffects && this.statusEffects.curse) atk = Math.floor(atk * 0.5);
    if (this.cls === 'monk' && this.passiveProc) { atk = Math.floor(atk * 1.5); this.passiveProc = false; }
    if (this._activeBuffs && this._activeBuffs.length > 0) {
      this._activeBuffs.forEach(b => { if (b.stat === 'atk') atk += b.val; });
    }
    if (typeof window.getAffixEffects === 'function') {
      const aff = window.getAffixEffects(this);
      if (aff.atk) atk += aff.atk;
      if (aff.atkPct) atk = Math.floor(atk * (1 + aff.atkPct));
    }
    return atk;
  }

  // ── 计算有效防御力 ──
  getEffDef() {
    const stats = this._calcEquipStats();
    let def = this.def + stats.bonusDef;
    if (this._activeBuffs && this._activeBuffs.length > 0) {
      this._activeBuffs.forEach(b => { if (b.stat === 'def') def += b.val; });
    }
    return def;
  }

  // ── 计算有效速度 ──
  getEffSpd() {
    const stats = this._calcEquipStats();
    return this.spd + stats.bonusSpd;
  }

  // ── 计算智力加值 ──
  getIntBonus() {
    const stats = this._calcEquipStats();
    return stats.bonusInt / 10;
  }

  // ── 获取可用技能列表 ──
  getAvailableSkills() {
    const cls = CLASSES[this.cls];
    if (!cls || !cls.skills) return [];
    return cls.skills.map((sid, idx) => {
      const sk = (typeof SKILLS_DB !== 'undefined' ? window.SKILLS_DB : null) || {};
      const skill = sk[sid];
      if (!skill) return null;
      const isUpgraded = this._skillUpgrades[sid];
      const data = isUpgraded && skill.upgraded ? { ...skill, ...skill.upgraded, isUpgraded: true } : { ...skill, isUpgraded: false };
      const unlocked = this.level >= skill.unlockLv;
      const cooldown = this._skillCooldowns[sid] || 0;
      const hasMp = (this.mp || 0) >= (data.cost || 0);
      return { ...data, index: idx, unlocked, cooldown, hasMp, ready: unlocked && cooldown === 0 && hasMp };
    }).filter(Boolean);
  }

  // ── 获取单个技能数据 ──
  getSkillData(skillId) {
    const sk = (typeof SKILLS_DB !== 'undefined' ? window.SKILLS_DB : null) || {};
    const skill = sk[skillId];
    if (!skill) return null;
    if (!this._skillUpgrades) this._skillUpgrades = {};
    const isUpgraded = this._skillUpgrades[skillId];
    if (isUpgraded && skill.upgraded) return { ...skill, ...skill.upgraded, isUpgraded: true, id: skillId };
    return { ...skill, isUpgraded: false, id: skillId };
  }

  // ── 应用伤害 ──
  applyDamage(dmg) {
    if (this._invincTurns > 0) return 0;
    let actualDmg = dmg;
    if (this._shieldTurns > 0) {
      const absorbed = Math.min(dmg, Math.floor(this.maxHp * 0.3));
      actualDmg = dmg - absorbed;
      this._shieldTurns--;
    }
    actualDmg = Math.max(0, actualDmg);
    this.hp = Math.max(0, this.hp - actualDmg);
    return actualDmg;
  }

  // ── 治疗 ──
  heal(amount) {
    const before = this.hp;
    this.hp = Math.min(this.maxHp, this.hp + amount);
    return this.hp - before;
  }

  // ── 消耗 MP ──
  consumeMp(amount) {
    if (this.mp < amount) return false;
    this.mp -= amount;
    return true;
  }

  // ── 检查是否死亡 ──
  get isAlive() { return this.hp > 0; }
  get hpPct() { return Math.max(0, Math.round(this.hp / this.maxHp * 100)); }
  get mpPct() { return Math.max(0, Math.round((this.mp || 0) / (this.maxMp || 50) * 100)); }

  // ── 序列化（用于存档） ──
  toJSON() {
    return {
      name: this.name, cls: this.cls, level: this.level,
      gold: this.gold, exp: this.exp, expNeed: this.expNeed,
      fatigue: this.fatigue, maxFatigue: this.maxFatigue,
      hp: this.hp, maxHp: this.maxHp, mp: this.mp, maxMp: this.maxMp,
      atk: this.atk, def: this.def, spd: this.spd,
      equipment: this.equipment, inventory: this.inventory,
      ownedEquipIds: this.ownedEquipIds,
      enhanceLevels: this.enhanceLevels,
      weaponPenalties: this.weaponPenalties,
      altarBuff: this.altarBuff,
      statusEffects: this.statusEffects,
      passiveProc: this.passiveProc,
      npcBoosts: this.npcBoosts,
      _skillCooldowns: this._skillCooldowns,
      _skillUpgrades: this._skillUpgrades,
      _lastSkillId: this._lastSkillId,
      _activeBuffs: this._activeBuffs,
      _activeDebuffs: this._activeDebuffs,
      _summonData: this._summonData,
      _burnTurns: this._burnTurns, _burnDmg: this._burnDmg,
      _shieldTurns: this._shieldTurns, _invincTurns: this._invincTurns,
      _hotTurns: this._hotTurns, _hotHeal: this._hotHeal,
    };
  }

  // ── 内部：装备属性计算 ──
  _calcEquipStats() {
    let bonusAtk = 0, bonusDef = 0, bonusStr = 0, bonusInt = 0, bonusSpd = 0;
    if (!this.equipment) this.equipment = {};
    if (!this.weaponPenalties) this.weaponPenalties = {};
    this.weaponPenalties = {};
    const clsWeaponType = this.cls ? (CLASSES[this.cls]?.weaponType || null) : null;
    for (const slot in this.equipment) {
      const eqId = this.equipment[slot];
      const item = getEquipById(eqId);
      if (!item) continue;
      let atkMul = 1, defMul = 1, spdMul = 1;
      if (item.weaponType && clsWeaponType && item.weaponType !== clsWeaponType) {
        atkMul = 0.7; defMul = 0.7; spdMul = 0.7;
        this.weaponPenalties[slot] = true;
      }
      if (item.atk) bonusAtk += Math.floor(item.atk * atkMul);
      if (item.def) bonusDef += Math.floor(item.def * defMul);
      if (item.spd) bonusSpd += Math.floor(item.spd * spdMul);
      if (item.str) bonusStr += item.str;
      if (item.int) bonusInt += item.int;
    }
    if (this.enhanceLevels) {
      for (const slot in this.enhanceLevels) {
        const lv = this.enhanceLevels[slot];
        if (lv > 0) { bonusAtk += Math.floor((lv + 1) * 1.5); bonusDef += Math.floor(lv * 0.8); }
      }
    }
    if (this.altarBuff) { bonusAtk += 5; bonusDef += 3; }
    if (this.npcBoosts) { bonusAtk += (this.npcBoosts.atk || 0); bonusDef += (this.npcBoosts.def || 0); bonusSpd += (this.npcBoosts.spd || 0); }
    return { bonusAtk, bonusDef, bonusStr, bonusInt, bonusSpd };
  }
}

// ── 从普通对象升级为 Character 实例 ──
export function upgradeToCharacter(obj) {
  if (obj instanceof Character) return obj;
  const char = new Character(obj);
  // 复制 Character 未覆盖的非战斗字段
  for (const key in obj) {
    if (!(key in char)) {
      char[key] = obj[key];
    }
  }
  return char;
}

// ── 创建 NPC / 怪物模拟角色 ──
export function createNPC(data) {
  return new Character({
    name: data.name || '???',
    cls: data.cls || null,
    level: data.lv || 1,
    hp: data.hp || 100, maxHp: data.hp || 100,
    mp: data.mp || 0, maxMp: data.mp || 0,
    atk: data.atk || 5, def: data.def || 0, spd: data.spd || 0,
  });
}
